<template>
  <div>
    <div v-if="isLoaded" class="flex-chart ratio-7-4 mb-1">
      <div :id="chartId" class="ratio-content" />
    </div>
    <div class="d-flex justify-content-end">
      <a href="#" @click.prevent="openMapModal" class="d-flex align-items-center position-absolute" style="bottom: 16px; right: 20px;">
        <i class="fa fa-arrows-alt t-secondary mr-1" />
        <span class="t-600 t-sm">Enlarge</span>
      </a>
    </div>
    <spinner v-if="!isLoaded" :containerClass="`width--50px min-height--150px`" />
  </div>
</template>

<script>
import Spinner from '@/components/shared/Spinner'
import helpers from './helpers'

import * as am5 from '@amcharts/amcharts5'
import * as am5map from '@amcharts/amcharts5/map'

import am5geodataWorldLow from '@amcharts/amcharts5-geodata/worldLow'
import am5geodataUsaLow from '@amcharts/amcharts5-geodata/usaLow'

export default {
  name: 'basicMap',
  components: {
    Spinner
  },
  mixins: [helpers],
  props: {
    graphData: {
      type: Array,
      required: true
    },
    mapType: {
      type: String,
      required: false,
      default: ''
    },
    name: {
      type: String,
      required: true
    },
    filterName: {
      type: String,
      required: true
    },
    state: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      regions: [],
      isLogarithmic: true,
      normalizedCounts: false,
      noData: false,
      isLoaded: false
    }
  },
  mounted () {
    this.$nextTick(() => this.parseData())
  },
  watch: {
    graphData (newVal, oldVal) {
      if (JSON.stringify(newVal) !== JSON.stringify(oldVal) && newVal) {
        this.parseData()
      }
    }
  },
  computed: {
    hasActiveCountry () {
      if (this.regions) {
        return !!this.regions.find(c => c.active)
      }
      return false
    }
  },
  methods: {
    openMapModal () {
      this.$events.$emit('modal:filter:map:open', this.filterName, this.state, this.mapType)
    },
    parseData () {
      // if (this.state.keyword || !this.isEmpty(this.getActiveFilters(this.state.filters))) {
      //   this.isLogarithmic = false
      //   this.normalizedCounts = true
      // }
      this.isLoaded = false

      // counts of 0 seem to break the logarithmic heatmap when all values are fairly low
      let totalCount = 0
      this.regions = this.graphData.filter(c => c.count > 0).map(c => {
        totalCount += c.total
        const normalizedCount = c.count / c.total * 100

        if (this.mapType === 'countries') {
          return {
            ...c,
            id: c.value,
            normalizedCount: normalizedCount
          }
        } else if (this.mapType === 'US_states') {
          // Extract US state code
          const splitState = c.value.split('-')
          const code = splitState[splitState.length - 1].toUpperCase()
          return {
            ...c,
            id: `US-${code}`,
            normalizedCount: normalizedCount
          }
        }
      })

      const averageModifier = (totalCount / this.regions.length) * 0.05
      // Mark regions as lowData when applicable. These will not be taken into account for the min/max values and colors
      this.regions.forEach(region => {
        if (region.total < averageModifier) {
          region.lowData = true
        }
      })

      // Calculate min/max values for both absolute and normalised counts. Ignore low data regions
      let lowest = Number.POSITIVE_INFINITY
      let highest = Number.NEGATIVE_INFINITY
      let lowestNormalised = Number.POSITIVE_INFINITY
      let highestNormalised = Number.NEGATIVE_INFINITY
      this.regions.forEach(region => {
        if (region.lowData) return
        if (region.count < lowest) lowest = region.count
        if (region.count > highest) highest = region.count
        if (region.normalizedCount < lowestNormalised) lowestNormalised = region.normalizedCount
        if (region.normalizedCount > highestNormalised) highestNormalised = region.normalizedCount
      })
      this.lowest = lowest
      this.highest = highest
      this.lowestNormalised = lowestNormalised
      this.highestNormalised = highestNormalised

      this.isLoaded = true

      this.$nextTick(() => {
        this.renderGraph()
      })
    },
    renderGraph () {
      this.initRoot()

      this.root.numberFormatter.setAll({
        numberFormat: '#,###',
        numericFields: ['startText', 'startValue', 'count']
      })

      let chart = this.root.container.children.push(
        am5map.MapChart.new(this.root, {
          panX: 'none',
          panY: 'none',
          wheelX: 'none',
          wheelY: 'none',
          pinchZoom: false,
          layout: this.root.horizontalLayout,
          projection: this.mapType === 'US_states' ? am5map.geoAlbersUsa() : am5map.geoNaturalEarth1()
        })
      )

      // Create polygon series
      let polygonSeries = chart.series.push(
        am5map.MapPolygonSeries.new(this.root, {
          geoJSON: this.mapType === 'US_states' ? am5geodataUsaLow : am5geodataWorldLow,
          useGeoData: true,
          valueField: this.normalizedCounts ? 'normalizedCount' : 'count',
          calculateAggregates: true,
          exclude: ['AQ']
        })
      )

      polygonSeries.mapPolygons.template.setAll({
        tooltipText: '{name}: {count}',
        interactive: true,
        fill: am5.color(0xDCDCDC),
        toggleKey: 'active'
      })

      polygonSeries.mapPolygons.template.adapters.add('tooltipText', (text, target) => {
        const count = target.dataItem.dataContext.count
        const total = target.dataItem.dataContext.total
        if (count <= 0 || !count) return '{name}: no results'

        if (this.normalizedCounts) {
          const humanReadableIndex = this.getIndexAsString(this.state.index)
          return `${this.$options.filters.formatNumber(target.dataItem.dataContext.normalizedCount, target.dataItem.dataContext.normalizedCount > 9 ? 2 : 3)}% of all ${humanReadableIndex} in [bold]{name}[/] match your search
${count} ${humanReadableIndex} out of ${total}${target.dataItem.dataContext.lowData ? '\n[#666]Low amount of data; percentage may not be accurate[/]' : ''}`
        }
        return text
      })

      const tooltip = am5.Tooltip.new(this.root, {
        getFillFromSprite: false,
        getStrokeFromSprite: false,
        autoTextColor: false
      })

      tooltip.get('background').setAll({
        fill: am5.color(0xffffff),
        fillOpacity: 0.6
      })

      tooltip.label.setAll({
        fill: am5.color(0x000000),
        fontSize: 14
      })

      polygonSeries.set('tooltip', tooltip)

      // This is our custom  heatLegend color coding rules
      // Supports 2 types of heatlegends (active/inactive filters)
      // Also takes care of "edge case low data" regions by giving them a pattern
      polygonSeries.set('heatRules', [{
        target: polygonSeries.mapPolygons.template,
        dataField: 'value', // has to be 'value' for some reason, even though valueField defined above is `valueField: 'count'`
        key: 'fill',
        logarithmic: true,
        min: am5.color(0xD6F6FE),
        max: am5.color(0x009ABD),
        customFunction: (sprite, min, max, value) => {
          // Logarithmic math to manually set heatmap colors
          const context = sprite.dataItem.dataContext
          let step, realPosition, stepPosition
          if (this.normalizedCounts) {
            min = this.lowestNormalised
            max = this.highestNormalised
          } else {
            min = this.lowest
            max = this.highest
          }
          if (this.isLogarithmic) {
            const logMin = Math.log(min)
            const logMax = Math.log(max)
            const logValue = Math.log(value)
            step = 1 / 12 // # of steps in the logarithmic gradient
            realPosition = (logValue - logMin) / (logMax - logMin)

            stepPosition = step
            // Discrete steps for the color
            while (stepPosition < realPosition) {
              stepPosition += step
            }
          } else {
            stepPosition = value / (max - min)
          }

          // Color prototype used to access interpolation function
          const c = am5.color(0xff0000)
          if (!this.hasActiveCountry || context.active) {
            // Interpolate color position % between min and max color
            const d = c.constructor.interpolate(stepPosition <= 1 ? stepPosition : 1, am5.color(0xD6F6FE), am5.color(0x009ABD))
            sprite.set('fill', d)
          } else {
            const d = c.constructor.interpolate(stepPosition <= 1 ? stepPosition : 1, am5.color(0xCCCCCC), am5.color(0x555555))
            sprite.set('fill', d)
          }
          // Have an accent for regions with low data
          if (this.normalizedCounts && context.lowData) {
            sprite.set('fillPattern', am5.LinePattern.new(this.root, {
              color: am5.color(0xcccccc),
              colorOpacity: 0.4,
              rotation: 45,
              width: 512,
              height: 512,
              gap: 5
            }))
          }
        }
      }])

      polygonSeries.data.setAll(this.regions)

      this.chart = chart
      this.polygonSeries = polygonSeries
    }
  }
}
</script>
